import React, { useEffect, useMemo, useState } from 'react';
import {
  StyledCode,
  StyledCodeContainer,
  StyledCopiedTag,
  StyledCreditCardContainer,
  StyledDiscountContainer,
  StyledTag
} from './style';
import { StyledDiv, StyledSpan } from '../../../Theme/CommonStyled';
import Icon from '../../Common/Icon';
import Button from '../../Common/Button';
import { CreditDataType } from '../../../Types/credits.type';
import { fixLocaleString } from '../../../Utils/common.util';
import { calculateCreditDays } from '../../../Utils/credits.util';
import { useTranslation } from 'react-i18next';
import useLandingStore from '../../../Store/Landing';
import { useNavigate } from 'react-router';
import useCreditsStore from '../../../Store/Credits';

const CreditCard: React.FC<{ data: CreditDataType; cardType: string }> = ({
  data,
  cardType
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const landingData = useLandingStore((state) => state.landingData);
  const setDataLength = useCreditsStore((state) => state.setDataLength);

  const { currency } = landingData;

  const [codeIsCopied, setCodeIsCopied] = useState(false);

  useEffect(() => {
    const codeCopyHandler = setTimeout(() => {
      codeIsCopied && setCodeIsCopied(false);
    }, 3000);

    return () => clearTimeout(codeCopyHandler);
  }, [codeIsCopied]);

  const remainingDate = useMemo(
    () => calculateCreditDays(data?.creditExpirationDate),
    [data]
  );

  const isHistory = useMemo(
    () => cardType === 'expired' || cardType === 'used',
    [cardType]
  );

  return (
    <StyledCreditCardContainer
      onClick={() => {
        setDataLength(10);
        data?.activityListSummaries?.length > 1
          ? navigate('/subCredits', { state: { data } })
          : navigate(`/credit/${data?.activityListSummaries[0].id}`, {
              state: { code: data?.code }
            });
      }}>
      <StyledDiv
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={7}>
        <StyledSpan
          fontSize={2}
          color={
            cardType === 'used'
              ? 'disableColor'
              : cardType === 'expired'
              ? 'danger'
              : remainingDate <= 3
              ? 'danger'
              : 'primaryBlue'
          }>
          {cardType === 'active'
            ? remainingDate > 0
              ? t('credits.creditCard.canBeUsedForAnotherDays', {
                  days: remainingDate
                })
              : t('credits.creditCard.lastDayOfUse')
            : t(`credits.${cardType}`)}
        </StyledSpan>
        <Icon name="enter-1" fontSize={4} color="minorText" />
      </StyledDiv>
      {data?.basePercentageCount > 0 && (
        <StyledDiscountContainer
          disabled={data?.percentageCount === 0 || isHistory}>
          <StyledDiv
            display="flex"
            width="100%"
            alignItems="center"
            justifyContent="space-between">
            <StyledDiv display="flex" alignItems="center" mb={5}>
              <Icon name="discount" fontSize={20} color="normalText" />
              <StyledDiv
                fontSize={10}
                fontWeight={8}
                display="flex"
                alignItems="center"
                mr={7}>
                {data?.percentage}
                <StyledSpan mr={3} fontSize={0}>
                  {t('global.percentage')}
                </StyledSpan>
              </StyledDiv>
            </StyledDiv>
            {data?.percentageCount === 0 && (
              <StyledSpan fontSize={2} color="disableColor">
                {t('credits.creditCard.used')}
              </StyledSpan>
            )}
          </StyledDiv>
          <StyledDiv display="flex">
            <StyledTag
              type="discount"
              ml={4}
              disabled={data?.percentageCount === 0 || isHistory}>
              {data?.maxDiscountPrice === 0
                ? t('credits.noCeiling')
                : t('credits.creditCard.maxDiscountPrice', {
                    price: fixLocaleString(data?.maxDiscountPrice),
                    currency
                  })}
            </StyledTag>
            {data?.percentageCount > 0 && (
              <StyledTag type="percentage" disabled={isHistory}>
                {t('credits.creditCard.usageCount', {
                  usageCount: data?.percentageCount
                })}
              </StyledTag>
            )}
          </StyledDiv>
        </StyledDiscountContainer>
      )}
      {data?.baseCredit > 0 && (
        <StyledDiscountContainer disabled={data?.credit === 0 || isHistory}>
          <StyledDiv display="flex" alignItems="center">
            <Icon name="credit" fontSize={20} color="normalText" />
            <StyledDiv
              fontSize={10}
              fontWeight={8}
              display="flex"
              alignItems="center"
              mr={7}>
              {fixLocaleString(data?.credit)}
              <StyledSpan mr={3} fontSize={0}>
                {currency}
              </StyledSpan>
            </StyledDiv>
          </StyledDiv>
        </StyledDiscountContainer>
      )}
      <StyledDiv display="flex" whiteSpace="nowrap" flexWrap="wrap">
        <StyledTag type="info" ml={4} mb={3}>
          {data?.validBranchName !== null && (
            <Icon name="home" fontSize={4} color="normalText" ml={6} />
          )}
          {data?.validBranchName === null
            ? t('credits.creditCard.validInAllBranches')
            : t('credits.creditCard.justInBranch', {
                branch: data?.validBranchName
              })}
        </StyledTag>
        <StyledTag type="info" ml={4} mb={3}>
          {data?.brandUseLimitationName}
        </StyledTag>
      </StyledDiv>
      {cardType === 'active' && (
        <StyledDiv display="flex" gap={8} mt={7}>
          <StyledCodeContainer disabled={isHistory}>
            <StyledSpan
              color={isHistory ? 'disableColor' : 'normalText'}
              fontSize={4}>
              {t('credits.creditCard.code')}
            </StyledSpan>
            <StyledCopiedTag codeIsCopied={codeIsCopied} bg="mainBackground">
              <Icon name="check" fontSize={10} color="secondaryGreen" ml={6} />
              <StyledSpan color="normalText" fontSize={2}>
                {t('global.copied')}
              </StyledSpan>
            </StyledCopiedTag>

            <StyledCode isHistory={isHistory} codeIsCopied={codeIsCopied}>
              {data?.code}
            </StyledCode>
          </StyledCodeContainer>
          <Button
            disabled={isHistory}
            width="54px"
            height="54px"
            p="unset"
            btnType="blue"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(String(data?.code));
              setCodeIsCopied(true);
            }}>
            <Icon
              name={'duplicate'}
              color="secondaryButtonText"
              fontSize={14}
            />
          </Button>
        </StyledDiv>
      )}
    </StyledCreditCardContainer>
  );
};

export default CreditCard;
